<template>
	<div>
		<div v-if="enhancedSearchEnabled" class="full-height">
			<div class="filter search-bar" v-bind="config.attrs" @keydown.up.prevent="focusPrevButton()"
				@keydown.down.prevent="focusNextButton()">
				<config-style-injector :config="config" :root-selector="`.search-bar`" />
				<input ref="search-input" type="text" :placeholder="content.searchFlyer" v-model="value"
					@input="handleInputChange" @focus="handleFocus" @blur="hideDropdown" @keypress.enter="handleSubmit(value)">
				<span v-if="value" class="clear" @click="clearSearch">
					<img alt="clear" src="/images/icon-close.svg">
				</span>
				<div v-if="value && value.length > 1" class="dropdown" v-show="dropdownVisible && hasResults"
					@focusin="handleFocus" @focusout="hideDropdown" ref="dropdown">
					<ul v-for="(indexResults, indexName) in filteredResults" :key="indexName">
						<li v-if="indexResults && indexResults.length">
							<h3>{{ formatName(indexName) }}</h3>
							<ul>
								<li>
									<button v-for="(hit, i) in indexResults" :key="hit.objectID" class="query-hit"
										@click="handleSuggestionClick(hit)" @focus="handleButtonFocus(i)">
										<span v-if="hit.titleEn" v-html="highlightResult(hit, 'titleEn')"></span>
										<span v-else v-html="highlightResult(hit, 'query')"></span>
									</button>
								</li>
							</ul>
						</li>
					</ul>
				</div>
				<button aria-label="search" class="search-icon" @click="handleSubmit(value)">
					<svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
						<g>
							<path
								d="M495,466.2L377.2,348.4c29.2-35.6,46.8-81.2,46.8-130.9C424,103.5,331.5,11,217.5,11C103.4,11,11,103.5,11,217.5   S103.4,424,217.5,424c49.7,0,95.2-17.5,130.8-46.7L466.1,495c8,8,20.9,8,28.9,0C503,487.1,503,474.1,495,466.2z M217.5,382.9   C126.2,382.9,52,308.7,52,217.5S126.2,52,217.5,52C308.7,52,383,126.3,383,217.5S308.7,382.9,217.5,382.9z"
								fill="currentColor" />
						</g>
					</svg>
				</button>
			</div>
		</div>
		<div v-else class="full-height">
			<div class="filter search-bar" v-bind="config.attrs">
				<config-style-injector :config="config" :root-selector="`.search-bar`" />
				<input ref="search-input" v-model="value" :aria-label="content.searchFlyer" :placeholder="content.searchFlyer"
					class="search-input" tabindex="0" type="text" @blur="setSearchFocusState(false)"
					@focus="setSearchFocusState(true)" @keyup.enter="handleSubmit(value)" />
				<span v-if="value" class="clear" @click="clearSearch">
					<img alt="clear" src="/images/icon-close.svg">
				</span>
				<button aria-label="search" class="search-icon" @click="handleSubmit(value)">
					<svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
						<g>
							<path
								d="M495,466.2L377.2,348.4c29.2-35.6,46.8-81.2,46.8-130.9C424,103.5,331.5,11,217.5,11C103.4,11,11,103.5,11,217.5   S103.4,424,217.5,424c49.7,0,95.2-17.5,130.8-46.7L466.1,495c8,8,20.9,8,28.9,0C503,487.1,503,474.1,495,466.2z M217.5,382.9   C126.2,382.9,52,308.7,52,217.5S126.2,52,217.5,52C308.7,52,383,126.3,383,217.5S308.7,382.9,217.5,382.9z"
								fill="currentColor" />
						</g>
					</svg>
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Lytics from "@/classes/Lytics";
import featureMixin from "@/mixins/featureMixin";
import ConfigStyleInjector from "@/components/style-injector/ConfigStyleInjector.vue";
import AppConfig from "@/classes/AppConfig";
import algoliasearch from 'algoliasearch/lite';

export default {
	name: "SearchBarFilter",
	components: { ConfigStyleInjector },

	mixins: [featureMixin],

	data() {
		return {
			value: '',
			dropdownVisible: false,
			blurTimeout: null,
			searchResults: {},
			searchClient: null,
			indices: [],
			focusedButton: -1,
			debounceTimeout: null
		}
	},

	props: {
		hidden: {
			type: Boolean,
			default: false
		}
	},

	created() {
		// Initialize Algolia client
		const appId = process.env.VUE_APP_ALGOLIA_APP_ID;
		const apiKey = process.env.VUE_APP_ALGOLIA_SEARCH_KEY;

		if (appId && apiKey) {
			this.searchClient = algoliasearch(appId, apiKey);

			// Initialize needed indices
			this.indices = [
				`products_${this.language}_${AppConfig?.AppConf?.algolia_id}_query_suggestions`,
				`categories_${this.language}_${this.banner}`
			];
		}
	},

	mounted() {
		this.value = this._config.value;
	},

	computed: {
		...mapGetters(['content', 'highlightResults', 'language']),
		...mapGetters('filters', ["searchQuery"]),
		...mapGetters('config', ['features']),
		...mapGetters('landingPage', ['currentFlyer']),

		enhancedSearchEnabled() {
			return this.features.enhancedSearch;
		},

		banner() {
			return AppConfig?.AppConf?.algolia_id;
		},

		hasResults() {
			return Object.values(this.filteredResults).some(results => results && results.length > 0);
		},

		filteredResults() {
			const filtered = {};

			// Only include indices we care about
			for (const indexName of this.indices) {
				if (this.searchResults[indexName] && this.searchResults[indexName].length > 0) {
					filtered[indexName] = this.searchResults[indexName];
				}
			}

			return filtered;
		}
	},

	methods: {
		...mapActions(['setSearchInputFocusState', 'toggleSideBarState']),
		...mapActions('events', ['triggerEvent']),
		...mapActions('search', ['showSearchResults', 'queryAlgolia', 'resetQuery']),

		async handleInputChange() {
			// Debounce search to avoid too many API calls
			clearTimeout(this.debounceTimeout);

			if (this.value && this.value.length > 1) {
				this.debounceTimeout = setTimeout(() => {
					this.performSearch(this.value);
				}, 200);
			} else {
				this.searchResults = {};
			}
		},

		async performSearch(query) {
			console.log('Performing search:', query);
			if (!this.searchClient || !query) return;

			try {
				const results = {};

				// Search in all required indices
				for (const indexName of this.indices) {
					const index = this.searchClient.initIndex(indexName);

					let filters = '';
					// Apply filters for categories index
					if (indexName.includes('categories') && this.currentFlyer && this.currentFlyer.id) {
						filters = `flyerIds:${this.currentFlyer.id}`;
					}

					const response = await index.search(query, {
						filters,
						hitsPerPage: 5
					});

					results[indexName] = response.hits;
				}

				this.searchResults = results;
				this.dropdownVisible = true;
			} catch (error) {
				console.error('Algolia search error:', error);
			}
		},

		clearSearch() {
			this.value = '';
			this._config.value = '';
			this.resetQuery();
			this.searchResults = {};
		},

		handleSuggestionClick(hit) {
			this.dropdownVisible = false;
			this.value = hit.titleEn || hit.query;
			this.handleSubmit(this.value);
		},

		handleSubmit(val) {
			this.queryAlgolia(val);
			this.$refs['search-input'].blur();
			this._config.value = val;
			console.log('Search Query', val);
			Lytics.TriggerSearch(val);
			this.triggerEvent({
				event: `filter-event-${this.config.name}`,
				data: val
			});
		},

		setSearchFocusState(val) {
			this.setSearchInputFocusState(val)
			this.toggleSideBarState(false);
		},

		hideDropdown() {
			// Check if the input field is the active element
			if (document.activeElement === this.$refs['search-input']) {
				return;
			}

			// Use setTimeout to delay hiding the dropdown, so the click event on the dropdown item can be fired
			this.blurTimeout = setTimeout(() => {
				this.dropdownVisible = false;
			}, 100);
		},

		handleFocus() {
			clearTimeout(this.blurTimeout);
			this.dropdownVisible = true;

			// If we already have a search term, perform search again to show dropdown
			if (this.value && this.value.length > 1) {
				this.performSearch(this.value);
			}
		},

		formatName(name) {
			if (name.startsWith('products_')) {
				return this.content.search.didYouMean;
			}
			if (name.startsWith('categories_')) {
				return this.content.search.categories;
			}
			return '';
		},

		highlightResult(hit, field) {
			if (!hit || !hit._highlightResult || !hit._highlightResult[field]) {
				return hit[field];
			}

			return hit._highlightResult[field].value;
		},

		focusNextButton() {
			if (!this.$refs['dropdown']) return;

			const buttons = this.$refs['dropdown'].querySelectorAll('button');
			const focusedElement = document.activeElement;
			const index = Array.prototype.indexOf.call(buttons, focusedElement);

			if (index >= 0 && index < buttons.length - 1) {
				buttons[index + 1].focus();
			} else if (index === -1) {
				buttons[0].focus();
			}
		},

		focusPrevButton() {
			if (!this.$refs['dropdown']) return;

			const buttons = this.$refs['dropdown'].querySelectorAll('button');
			const focusedElement = document.activeElement;
			const index = Array.prototype.indexOf.call(buttons, focusedElement);

			if (index > 0) {
				buttons[index - 1].focus();
			}
		},

		handleButtonFocus(index) {
			this.focusedButton = index;
		}
	},

	watch: {
		searchQuery(val) {
			console.log('search query update:', val);
			this.value = this.config.value;
		}
	}
}
</script>

<style lang="scss" scoped>
.clear {
	position: absolute;
	right: 40px;
	top: 51%;
	transform: translateY(-50%);
	line-height: 1em;
	font-family: monospace;
	cursor: pointer;
}

.search-bar {
	display: flex;
	position: relative;
	min-width: 130px;
	/* flex-grow: 1; */
	min-height: 34px;
	height: 100%;
}

.search-bar input {
	flex-basis: 100%;
	border: 1px solid #AFAFAF;
	border-radius: 4px;
	;
	/* border-color: var(--tertiary-color); */
	font-size: 16px;
	/*height: 35px;*/
	text-overflow: ellipsis;
	padding-left: 1em;
	padding-right: 1em;
	width: 100%;
}

.search-bar .search-icon {
	width: 30px;
	color: #fff;
	background-color: var(--secondary-color);
	border: none;
	border-radius: 4px;
	position: absolute;
	right: 0;
	height: 100%;
	min-height: auto;
  display: flex;
  justify-content: center;

	svg {
		width: 80%
	}

}

.search-bar .search-icon:hover {
	filter: brightness(90%);
}


li {
	list-style: none;
	padding: 0;
	margin-bottom: 5px;
}

h3 {
	margin: 0;
	margin-bottom: 10px;
	font-weight: bold;
}

.search-bar button {
	border: none;
	width: 100%;
	justify-content: start;
	text-align: left;
	min-height: auto;
}

.dropdown {
	position: absolute;
	top: 103%;
	left: 0;
	right: 0;
	background: white;
	border: 1px solid #aaa;
	z-index: 104;
	padding: 15px 8px;
	border-bottom-left-radius: 4px;
	border-bottom-right-radius: 4px;
	box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.2);

	li button {
		border: none !important;
		justify-content: start;
		flex-direction: row;

		span {
			text-align: left;
			text-wrap: wrap;
			font-size: 1.2em;
		}
	}
}

.full-height {
	height: 100%;
}
</style>

<style>
.query-hit em {
	font-style: normal;
	color: var(--secondary-color);
	background: none;
}
</style>